import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Mail, Pause, Play, AlertTriangle, Plus, Edit3, Trash2, Send, Eye,
  CheckCircle2, Loader2, X, Users, Save, FileText, Sparkles, ChevronRight,
} from 'lucide-react';

/**
 * Email Campaigns admin page.
 * Three tabs:
 *   • Settings   — global pause/unpause kill-switch + 7-day stats
 *   • Templates  — reusable subject/HTML pairs with {{variable}} placeholders
 *   • Campaigns  — one-shot blasts: pick template + audience + send
 */
const TABS = [
  { k: 'settings', l: 'Settings', icon: Pause },
  { k: 'templates', l: 'Templates', icon: FileText },
  { k: 'campaigns', l: 'Campaigns', icon: Send },
];

export default function EmailCampaignsPage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('settings');
  return (
    <div className="space-y-4" data-testid="email-campaigns-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
          <Mail className="w-6 h-6 text-cyan-400" /> Email Campaigns
        </h1>
        <p className="text-xs text-zinc-400 mt-1">
          Pause / resume all outbound mail · manage reusable templates · send one-shot campaigns.
        </p>
      </div>

      <div className="flex gap-1 border-b border-zinc-800">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`email-tab-${t.k}`}
            className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
              tab === t.k ? 'border-cyan-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {tab === 'settings' && <SettingsTab api={api} />}
      {tab === 'templates' && <TemplatesTab api={api} />}
      {tab === 'campaigns' && <CampaignsTab api={api} />}
    </div>
  );
}

// ──────────── Settings tab ────────────
function SettingsTab({ api }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');

  const load = () => api.get('/admin/email/settings').then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const togglePause = async () => {
    setBusy(true);
    try {
      if (data.paused) await api.post('/admin/email/resume');
      else await api.post('/admin/email/pause', { reason: reason || 'Manual pause by admin' });
      await load();
    } finally { setBusy(false); }
  };

  // Founder ask May 14, 2026: pause just the bots (digests, auto
  // reminders, shot-assigned, submission-created) to save the Resend
  // quota while keeping welcome / password reset / manual sends alive.
  const [botsBusy, setBotsBusy] = useState(false);
  const [botsReason, setBotsReason] = useState('');
  const toggleBots = async () => {
    setBotsBusy(true);
    try {
      await api.post('/admin/email/pause-bots', {
        paused: !data.bots_paused,
        reason: botsReason || 'Bots paused to save Resend quota',
      });
      await load();
    } finally { setBotsBusy(false); }
  };

  if (!data) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const stats = data.stats_7d || {};

  return (
    <div className="space-y-4">
      <div className={`rounded-md p-5 border ${data.paused ? 'bg-amber-500/10 border-amber-500/40' : 'bg-emerald-500/5 border-emerald-500/30'}`}
           data-testid="email-pause-card">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${data.paused ? 'bg-amber-500/30' : 'bg-emerald-500/20'}`}>
            {data.paused ? <Pause className="w-6 h-6 text-amber-200" /> : <Play className="w-6 h-6 text-emerald-300" />}
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-heading font-semibold text-white">
              {data.paused ? 'All outbound email is PAUSED' : 'Email sending is LIVE'}
            </h3>
            <p className="text-xs text-zinc-300 mt-1">
              {data.paused
                ? `Every send attempt is logged but NOT delivered. Reason: ${data.pause_reason || '—'}`
                : 'Transactional emails (welcome / approvals / digests) and campaigns will be delivered via Resend.'}
            </p>
            {data.paused && data.paused_at && (
              <p className="text-[10px] text-zinc-500 mt-1">
                Paused {new Date(data.paused_at).toLocaleString()} by {data.paused_by}
              </p>
            )}
          </div>
        </div>

        {!data.paused && (
          <div className="mt-4 flex items-center gap-2">
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (e.g. Resend credits exhausted)"
              data-testid="email-pause-reason"
              className="flex-1 h-8 px-3 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-amber-500/50" />
          </div>
        )}
        <div className="mt-4">
          <button onClick={togglePause} disabled={busy} data-testid="email-pause-toggle"
            className={`h-9 px-4 rounded-md text-xs font-medium flex items-center gap-2 transition ${
              data.paused
                ? 'bg-emerald-500 hover:bg-emerald-400 text-white'
                : 'bg-amber-500 hover:bg-amber-400 text-zinc-900'
            } disabled:opacity-40`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (data.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />)}
            {data.paused ? 'Resume Email Sending' : 'Pause All Email'}
          </button>
        </div>
      </div>

      {/* ───── BOTS-ONLY KILL SWITCH ─────
          Stops auto/system emails (digests, auto reminders,
          shot-assigned bursts) while still letting welcomes / password
          resets / manual sends through. Lifesaver when Resend quota is
          running thin. */}
      <div className={`rounded-md p-5 border ${data.bots_paused ? 'bg-violet-500/10 border-violet-500/40' : 'bg-zinc-900 border-zinc-800'}`}
           data-testid="email-bots-pause-card">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${data.bots_paused ? 'bg-violet-500/30' : 'bg-zinc-800'}`}>
            {data.bots_paused ? <Pause className="w-6 h-6 text-violet-200" /> : <Play className="w-6 h-6 text-zinc-400" />}
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-heading font-semibold text-white flex items-center gap-2">
              {data.bots_paused ? 'BOT EMAILS PAUSED' : 'Bot emails are ACTIVE'}
              <span className="text-[9px] uppercase tracking-wider text-violet-300 bg-violet-500/15 px-1.5 py-0.5 rounded">
                Founder requested
              </span>
            </h3>
            <p className="text-xs text-zinc-300 mt-1">
              {data.bots_paused
                ? `Automated emails (daily digests, shot-assigned bursts, submission alerts) are silenced. Human-triggered emails (welcomes, password resets, manual sends) still go through. Reason: ${data.bots_pause_reason || '—'}`
                : 'Bot emails (daily digests, shot-assigned alerts, submission notifications) are sending normally. Pause this if your Resend quota is running thin — welcomes and manual sends will keep working.'}
            </p>
            {data.bots_paused && data.bots_paused_at && (
              <p className="text-[10px] text-zinc-500 mt-1">
                Paused {new Date(data.bots_paused_at).toLocaleString()} by {data.bots_paused_by}
              </p>
            )}
          </div>
        </div>
        {!data.bots_paused && (
          <div className="mt-4 flex items-center gap-2">
            <input value={botsReason} onChange={e => setBotsReason(e.target.value)}
                   placeholder="Reason (e.g. Resend quota low, paused bots)"
                   data-testid="email-bots-pause-reason"
                   className="flex-1 h-8 px-3 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-violet-500/50" />
          </div>
        )}
        <div className="mt-4">
          <button onClick={toggleBots} disabled={botsBusy} data-testid="email-bots-pause-toggle"
            className={`h-9 px-4 rounded-md text-xs font-medium flex items-center gap-2 transition ${
              data.bots_paused
                ? 'bg-emerald-500 hover:bg-emerald-400 text-white'
                : 'bg-violet-500 hover:bg-violet-400 text-white'
            } disabled:opacity-40`}>
            {botsBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (data.bots_paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />)}
            {data.bots_paused ? 'Resume bot emails' : 'Pause bot emails (keep manual sends alive)'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-2">Last 7 days</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Sent" v={stats.sent || 0} c="text-emerald-300" />
          <Stat label="Paused (skipped)" v={stats.paused || 0} c="text-amber-300" />
          <Stat label="Failed" v={stats.failed || 0} c="text-rose-300" />
          <Stat label="No-key skip" v={stats.skipped_no_key || 0} c="text-zinc-300" />
        </div>
      </div>

      {/* ── PER-EVENT TOGGLES ─── */}
      <EventTogglesPanel api={api} />
    </div>
  );
}

// ──────────── Per-event toggles ────────────
// Founder ask May 15, 2026: "Create a toggle button in front of each
// event so I can choose what event triggers a mail and when."
function EventTogglesPanel({ api }) {
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState({}); // {event_key: bool}

  const load = React.useCallback(() => {
    api.get('/admin/email/event-toggles').then(r => setEvents(r.data.events)).catch(() => setEvents([]));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const flip = async (ev) => {
    setBusy(b => ({ ...b, [ev.key]: true }));
    try {
      await api.put(`/admin/email/event-toggles/${ev.key}`, { enabled: !ev.enabled });
      setEvents(list => list.map(e => e.key === ev.key ? { ...e, enabled: !ev.enabled } : e));
    } catch (e) {
      alert(e.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusy(b => ({ ...b, [ev.key]: false }));
    }
  };

  if (!events) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const groups = [
    { id: 'bot', label: 'Automated / Bot emails',
      sub: 'High-volume, safe to disable when quota is tight.',
      tone: 'violet' },
    { id: 'transactional', label: 'Transactional emails',
      sub: 'Triggered by user actions. Keep ON unless you have a strong reason.',
      tone: 'emerald' },
  ];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="email-event-toggles">
      <h3 className="text-sm font-heading font-semibold text-white">Per-event email triggers</h3>
      <p className="text-[11px] text-zinc-400 mt-1">
        Toggle individual events. A disabled event is still logged (as <code className="text-violet-300">event_disabled</code>) so you can audit what would have sent.
      </p>

      {groups.map(g => {
        const rows = events.filter(e => e.category === g.id);
        if (rows.length === 0) return null;
        const onCount = rows.filter(r => r.enabled).length;
        const accent = g.tone === 'violet' ? 'text-violet-300' : 'text-emerald-300';
        return (
          <div key={g.id} className="mt-5">
            <div className="flex items-baseline justify-between">
              <h4 className={`text-[10px] uppercase tracking-widest font-bold ${accent}`}>{g.label}</h4>
              <span className="text-[10px] text-zinc-500">{onCount} / {rows.length} enabled</span>
            </div>
            <p className="text-[11px] text-zinc-500">{g.sub}</p>
            <div className="mt-2 divide-y divide-zinc-800/60 border border-zinc-800 rounded-md">
              {rows.map(ev => (
                <div key={ev.key}
                     className="flex items-center gap-3 px-3 py-2.5"
                     data-testid={`email-event-row-${ev.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-zinc-100 font-medium">{ev.label}</span>
                      <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">
                        {ev.key}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-0.5 truncate" title={ev.description}>
                      {ev.description}
                    </p>
                  </div>
                  <button
                    onClick={() => flip(ev)}
                    disabled={busy[ev.key]}
                    data-testid={`email-event-toggle-${ev.key}`}
                    title={ev.enabled ? 'Click to DISABLE this event' : 'Click to ENABLE this event'}
                    className={`relative h-6 w-11 rounded-full transition-colors flex-shrink-0 ${
                      ev.enabled ? 'bg-emerald-500' : 'bg-zinc-700'
                    } disabled:opacity-50`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                        ev.enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ──────────── Templates tab ────────────
function TemplatesTab({ api }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // template object or 'new'

  const load = () => api.get('/admin/email/templates').then(r => setItems(r.data.templates));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const seed = async () => {
    await api.post('/admin/email/templates/seed');
    await load();
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this template? Campaigns using it will fail at send time.')) return;
    await api.delete(`/admin/email/templates/${id}`);
    await load();
  };

  if (!items) return <Loader2 className="w-5 h-5 animate-spin" />;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button onClick={() => setEditing('new')} data-testid="email-template-new-btn"
          className="h-8 px-3 bg-cyan-500 hover:bg-cyan-400 text-white text-xs rounded-md flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> New Template
        </button>
        {items.length === 0 && (
          <button onClick={seed} data-testid="email-template-seed-btn"
            className="h-8 px-3 bg-violet-500/20 text-violet-200 border border-violet-500/30 text-xs rounded-md flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Seed 3 starters
          </button>
        )}
        <span className="ml-auto text-[11px] text-zinc-500">{items.length} template{items.length === 1 ? '' : 's'}</span>
      </div>

      {items.length === 0 ? (
        <div className="p-12 bg-zinc-900 border border-dashed border-zinc-800 rounded-md text-center">
          <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-300">No templates yet</p>
          <p className="text-xs text-zinc-500 mt-1">Click "Seed 3 starters" above to get going quickly.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="email-templates-list">
          {items.map(t => (
            <div key={t.id} className="bg-zinc-900 border border-zinc-800 hover:border-cyan-500/40 rounded-md p-4 transition-all"
                 data-testid={`email-template-${t.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1">
                  <h4 className="text-xs font-semibold text-white">{t.name}</h4>
                  <span className="text-[10px] uppercase tracking-wider text-cyan-300">{t.category}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(t)} data-testid={`email-template-edit-${t.id}`}
                    className="p-1 text-zinc-400 hover:text-cyan-300"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(t.id)} data-testid={`email-template-delete-${t.id}`}
                    className="p-1 text-zinc-400 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <p className="text-[11px] text-zinc-400 truncate"><span className="text-zinc-500">Subject:</span> {t.subject}</p>
              {t.variables?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {t.variables.map(v => (
                    <code key={v} className="text-[9px] px-1.5 py-0.5 bg-zinc-800 text-amber-200 rounded">{`{{${v}}}`}</code>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateEditor api={api} initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function TemplateEditor({ api, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: '', category: 'general', subject: '', html: '', preview_text: '', variables: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [varInput, setVarInput] = useState('');

  const save = async () => {
    setBusy(true); setErr('');
    try {
      if (initial?.id) await api.put(`/admin/email/templates/${initial.id}`, form);
      else await api.post('/admin/email/templates', form);
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-[44rem] max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="email-template-editor">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm font-heading font-semibold text-white flex items-center gap-2">
            <FileText className="w-4 h-4 text-cyan-400" /> {initial ? 'Edit Template' : 'New Template'}
          </h3>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="email-tpl-name" />
            <SelectField label="Category" v={form.category} on={(v) => setForm({ ...form, category: v })}
              testid="email-tpl-category" opts={['general', 'welcome', 'approval', 'digest', 'reminder', 'announcement']} />
          </div>
          <Field label="Subject *" v={form.subject} on={(v) => setForm({ ...form, subject: v })}
            testid="email-tpl-subject" placeholder="e.g. Hi {{full_name}}, welcome to VawworldOS!" />
          <Field label="Preview Text" v={form.preview_text} on={(v) => setForm({ ...form, preview_text: v })}
            testid="email-tpl-preview" placeholder="One-liner shown next to subject in inbox" />
          <div>
            <label className="text-[10px] text-zinc-300 uppercase tracking-wider">HTML Body * (use {`{{var}}`} placeholders)</label>
            <textarea value={form.html} onChange={e => setForm({ ...form, html: e.target.value })} rows={10}
              data-testid="email-tpl-html"
              className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-[11px] text-white font-mono focus:outline-none focus:border-cyan-500/50" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-300 uppercase tracking-wider">Variables</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {form.variables.map((v, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 bg-amber-500/15 text-amber-200 rounded border border-amber-500/30 flex items-center gap-1">
                  {`{{${v}}}`}
                  <button onClick={() => setForm({ ...form, variables: form.variables.filter((_, j) => j !== i) })}
                    className="text-amber-400 hover:text-amber-100">×</button>
                </span>
              ))}
              <div className="flex gap-1">
                <input value={varInput} onChange={e => setVarInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && varInput.trim()) {
                      e.preventDefault();
                      setForm({ ...form, variables: [...form.variables, varInput.trim()] });
                      setVarInput('');
                    }
                  }}
                  placeholder="e.g. project_name + Enter" data-testid="email-tpl-var-input"
                  className="h-6 px-2 bg-zinc-950 border border-zinc-800 rounded text-[10px] text-white focus:outline-none w-32" />
              </div>
            </div>
          </div>
          {err && <p className="text-[11px] text-rose-300">{err}</p>}
        </div>
        <div className="p-4 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 text-zinc-300 hover:text-white">Cancel</button>
          <button onClick={save} disabled={busy || !form.name || !form.subject || !form.html} data-testid="email-tpl-save"
            className="text-xs px-4 py-1.5 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-30 rounded-md text-white font-medium flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save Template
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────── Campaigns tab ────────────
function CampaignsTab({ api }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get('/admin/email/campaigns').then(r => setItems(r.data.campaigns));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const send = async (cid) => {
    if (!window.confirm('Send this campaign now? Respects the global pause flag.')) return;
    await api.post(`/admin/email/campaigns/${cid}/send`);
    await load();
  };
  const remove = async (cid) => {
    if (!window.confirm('Delete this campaign?')) return;
    await api.delete(`/admin/email/campaigns/${cid}`);
    await load();
  };

  if (!items) return <Loader2 className="w-5 h-5 animate-spin" />;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button onClick={() => setCreating(true)} data-testid="email-campaign-new-btn"
          className="h-8 px-3 bg-cyan-500 hover:bg-cyan-400 text-white text-xs rounded-md flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> New Campaign
        </button>
        <span className="ml-auto text-[11px] text-zinc-500">{items.length} campaign{items.length === 1 ? '' : 's'}</span>
      </div>

      {items.length === 0 ? (
        <div className="p-12 bg-zinc-900 border border-dashed border-zinc-800 rounded-md text-center">
          <Send className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-300">No campaigns yet</p>
          <p className="text-xs text-zinc-500 mt-1">Create a template first, then build a campaign on top of it.</p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden" data-testid="email-campaigns-list">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-[10px] text-zinc-400 uppercase">
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Template</th>
                <th className="text-left px-4 py-2">Audience</th>
                <th className="text-right px-4 py-2">Recipients</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/40">
              {items.map(c => (
                <tr key={c.id} data-testid={`email-campaign-${c.id}`}>
                  <td className="px-4 py-2 text-xs text-zinc-100 font-medium">{c.name}</td>
                  <td className="px-4 py-2 text-xs text-zinc-300">{c.template_name}</td>
                  <td className="px-4 py-2 text-[11px] text-zinc-400">{describeAudience(c.audience)}</td>
                  <td className="px-4 py-2 text-xs text-zinc-100 font-mono text-right">{c.recipient_count}</td>
                  <td className="px-4 py-2 text-xs">
                    <StatusPill status={c.status} stats={c.stats} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      {c.status === 'draft' && (
                        <button onClick={() => send(c.id)} data-testid={`email-campaign-send-${c.id}`}
                          className="text-[10px] px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-300 rounded flex items-center gap-1">
                          <Send className="w-3 h-3" /> Send
                        </button>
                      )}
                      <button onClick={() => remove(c.id)} data-testid={`email-campaign-delete-${c.id}`}
                        className="text-[10px] p-1 text-zinc-400 hover:text-rose-300">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CampaignCreator api={api} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CampaignCreator({ api, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    name: '', template_id: '',
    audience: { role: '', workforce_type: '', emails: [] },
  });
  const [emailBuf, setEmailBuf] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/admin/email/templates').then(r => setTemplates(r.data.templates));
  }, [api]);

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const audience = {
        ...(form.audience.role ? { role: form.audience.role } : {}),
        ...(form.audience.workforce_type ? { workforce_type: form.audience.workforce_type } : {}),
        ...(form.audience.emails.length > 0 ? { emails: form.audience.emails } : {}),
      };
      await api.post('/admin/email/campaigns', { ...form, audience });
      onCreated();
    } catch (e) { setErr(e.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-[34rem]" onClick={e => e.stopPropagation()} data-testid="email-campaign-creator">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm font-heading font-semibold text-white flex items-center gap-2">
            <Send className="w-4 h-4 text-cyan-400" /> New Campaign
          </h3>
          <button onClick={onClose}><X className="w-4 h-4 text-zinc-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Campaign Name *" v={form.name} on={(v) => setForm({ ...form, name: v })} testid="campaign-name" />
          <SelectField label="Template *" v={form.template_id} on={(v) => setForm({ ...form, template_id: v })}
            testid="campaign-template" opts={[{ value: '', label: '— select —' }, ...templates.map(t => ({ value: t.id, label: t.name }))]} />
          <div className="border-t border-zinc-800 pt-3">
            <label className="text-[10px] text-zinc-300 uppercase tracking-wider flex items-center gap-1">
              <Users className="w-3 h-3" /> Audience (use any combination)
            </label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <SelectField label="Role" v={form.audience.role} on={(v) => setForm({ ...form, audience: { ...form.audience, role: v } })}
                testid="campaign-aud-role"
                opts={['', 'master_admin', 'production_manager', 'hr_manager', 'creative_director',
                  'supervisor', 'animator', 'modeler', 'lighter', 'compositor', 'freelancer',
                  'client_producer', 'client_director', 'vendor_producer', 'vendor_artist']} />
              <SelectField label="Workforce" v={form.audience.workforce_type}
                on={(v) => setForm({ ...form, audience: { ...form.audience, workforce_type: v } })}
                testid="campaign-aud-workforce" opts={['', 'employee', 'vendor', 'freelancer']} />
            </div>
            <div className="mt-3">
              <label className="text-[10px] text-zinc-300 uppercase tracking-wider">Or explicit emails (comma / Enter)</label>
              <input value={emailBuf} onChange={e => setEmailBuf(e.target.value)}
                onKeyDown={e => {
                  if ((e.key === 'Enter' || e.key === ',') && emailBuf.trim()) {
                    e.preventDefault();
                    setForm({ ...form, audience: { ...form.audience, emails: [...form.audience.emails, emailBuf.trim().replace(/,$/, '')] } });
                    setEmailBuf('');
                  }
                }}
                data-testid="campaign-aud-emails"
                className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none" />
              <div className="flex flex-wrap gap-1 mt-2">
                {form.audience.emails.map((e, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 bg-cyan-500/15 text-cyan-200 rounded flex items-center gap-1">
                    {e}
                    <button onClick={() => setForm({ ...form, audience: { ...form.audience, emails: form.audience.emails.filter((_, j) => j !== i) } })}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          {err && <p className="text-[11px] text-rose-300">{err}</p>}
        </div>
        <div className="p-4 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 text-zinc-300 hover:text-white">Cancel</button>
          <button onClick={submit} disabled={busy || !form.name || !form.template_id} data-testid="campaign-save"
            className="text-xs px-4 py-1.5 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-30 rounded-md text-white font-medium flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
            Create Draft
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────── Atoms ────────────
const Stat = ({ label, v, c }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3 text-center">
    <p className={`text-2xl font-heading font-bold ${c}`}>{v}</p>
    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</p>
  </div>
);

const Field = ({ label, v, on, testid, placeholder }) => (
  <div>
    <label className="text-[10px] text-zinc-300 uppercase tracking-wider">{label}</label>
    <input value={v ?? ''} onChange={e => on(e.target.value)} placeholder={placeholder}
      data-testid={testid}
      className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none focus:border-cyan-500/50" />
  </div>
);

const SelectField = ({ label, v, on, testid, opts }) => (
  <div>
    <label className="text-[10px] text-zinc-300 uppercase tracking-wider">{label}</label>
    <select value={v ?? ''} onChange={e => on(e.target.value)} data-testid={testid}
      className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white focus:outline-none">
      {opts.map(o => {
        const val = typeof o === 'string' ? o : o.value;
        const lbl = typeof o === 'string' ? (o ? o.replace(/_/g, ' ') : '— any —') : o.label;
        return <option key={val} value={val}>{lbl}</option>;
      })}
    </select>
  </div>
);

const StatusPill = ({ status, stats }) => {
  const map = {
    draft: 'bg-zinc-700/40 text-zinc-300',
    sending: 'bg-amber-500/20 text-amber-300',
    sent: 'bg-emerald-500/20 text-emerald-300',
    paused: 'bg-amber-500/20 text-amber-200',
    failed: 'bg-rose-500/20 text-rose-300',
  };
  return (
    <span className="flex items-center gap-1">
      <span className={`px-1.5 py-0.5 rounded text-[10px] capitalize ${map[status] || 'bg-zinc-800 text-zinc-300'}`}>{status}</span>
      {stats && status !== 'draft' && (
        <span className="text-[10px] text-zinc-500 font-mono">
          ✓{stats.sent || 0} ⏸{stats.paused || 0} ✗{stats.failed || 0}
        </span>
      )}
    </span>
  );
};

function describeAudience(a = {}) {
  const parts = [];
  if (a.role) parts.push(`role:${a.role}`);
  if (a.workforce_type) parts.push(`type:${a.workforce_type}`);
  if (a.client_org_id) parts.push(`org:${String(a.client_org_id).slice(0, 8)}`);
  if (a.project_id) parts.push(`project:${String(a.project_id).slice(0, 8)}`);
  if (a.emails?.length) parts.push(`+${a.emails.length} explicit`);
  return parts.length ? parts.join(' · ') : 'all (no filter)';
}
